import { Slide, LLMConfig } from '@/shared/types';

export const TINY_VIEWPORT_WIDTH = 500;
export const SMALL_VIEWPORT_WIDTH = 1370;

export const THRESHOLD_TO_SHOW_LOCAL_TIME_SAVED = 60; // 1 hour
export const CONVERSION_THRESHOLD_IN_MINUTES = 600; // 10 hours

export const IDLE_BUFFER_MS = 1000 * 10; // 10 seconds
export const IDLE_TIMEOUT_WARNING_MS = 1000 * 60 * 5; // 5 minutes
export const TOKEN_EXPIRATION_WARNING_MS = 1000 * 60 * 10; // 10 minutes

export const SOURCES_LIST_LIMIT = 500;

export const MAX_CONCURRENT_SOCKETS = 1000;

export const ENRICHMENT_POLL_INTERVAL_MS = 1500;
export const ENRICHMENT_POLL_TIMEOUT_MS = 1000 * 60 * 5;

export const SHARED_DATA_TYPES = ['captures', 'copies', 'notes', 'sources'];

/** Chat history rows still being written — keep polling until status moves past these (see answer-item intermediate statuses). */
export function isEnrichmentHistoryPending(status: unknown): boolean {
  return status === 'initializing' || status === 'inprogress';
}

export const META_FILE_REGEX = /^((preservation[-_]\d+)|(records))\.html$/i;

export const PROVENANCE_STATUS_GROUPS = {
  SUCCESS: ['verified', 'success', 'completed', 'valid', 'approved', 'good', 'ok', 'pass', 'delivery'],
  ERROR: ['failed', 'error', 'rejected', 'invalid', 'denied', 'bad', 'fail', 'reject'],
  INTERMEDIARY: ['pending', 'processing', 'in_progress', 'queued', 'waiting', 'process', 'active', 'running', 'ingestion', 'dispatch', 'analysis', 'wrote_bkb_metadata']
};

export const FileTypes = {
  // '12345': 'image', // we check a numeric extension when guessing media type
  'jpg': 'image',
  'jpeg': 'image',
  'dng': 'image',
  'png': 'image',
  'gif': 'image',
  'bmp': 'image',
  'tiff': 'image',
  'webp': 'image',
  'svg': 'image',
  'pic': 'image',
  'pic_compressed': 'image',
  'thumb': 'image',
  'thumbnail': 'image',
  'thumnail': 'image',
  'jp2': 'image',
  'jxl': 'image',
  'heic': 'image',
  'heif': 'image',
  'wmf': 'image',
  'avif': 'image',
  'aac': 'audio',
  'mp3': 'audio',
  'm4a': 'audio',
  'wav': 'audio',
  'ogg': 'audio',
  'amr': 'audio',
  'slk': 'audio',
  'opus': 'audio',
  'wma': 'audio',
  'mp4': 'video',
  'm4v': 'video',
  'webm': 'video',
  'wmv': 'video',
  'mpg': 'video',
  'mpeg': 'video',
  'mov': 'video',
  'avi': 'video',
  'mkv': 'video',
  'flv': 'video',
  'dav': 'video',
  'pdf': 'pdf',
  'docx': 'docx',
  'doc': 'docx',
  'xls': 'spreadsheet',
  'xlsx': 'spreadsheet',
  'ppt': 'presentation',
  'pptx': 'markdown',
  'txt': 'text',
  'text': 'text',
  'xml': 'text',
  'md': 'markdown',
  'markdown': 'markdown',
  'msg': 'text',
  'eml': 'text',
  'mbox': 'text',
  'mbx': 'text',
  'pst': 'text',
  'emlx': 'text',
  'email': 'text',
  'pdfa': 'pdf',
  'vtt': 'text',
  'csv': 'spreadsheet',
  'tsv': 'spreadsheet',
  'html': 'html',
  'htm': 'html',
  'website': 'html',
  'url': 'html',
}

// we now convert all videos and audio that can be converted, and the proxy route checks for the converted file
// export const convertedVideo = ['avi', 'mkv', 'flv'];

export const fileTypeControllers = {
  'PDF': ['pdf', 'pdfa'],
  'MS Office': ['pptx', 'docx', 'doc', 'xls', 'xlsx'],
  'Image': Object.keys(FileTypes).filter((key) => (FileTypes as any)[key] === 'image').map((key) => key.toLowerCase()),
  'Video': Object.keys(FileTypes).filter((key) => (FileTypes as any)[key] === 'video').map((key) => key.toLowerCase()),
  'Audio': Object.keys(FileTypes).filter((key) => (FileTypes as any)[key] === 'audio').map((key) => key.toLowerCase()),
  'Spreadsheet': ['xls', 'xlsx', 'csv', 'tsv'],
  'Email': ['msg', 'eml', 'mbox', 'mbx', 'pst', 'emlx', 'msg', 'email', 'pdfa'],
  'Other': ['txt', 'html', 'website', 'url'],
}

export const UsableMediaTypes = ['image', 'video', 'audio', 'pdf', 'text', 'markdown', 'docx', 'html', 'spreadsheet'];
// don't allow the download of these file types
export const RestrictedSourceTypes = [
  'ps1', 'ps2', 'psm1', 'psm2', 'psm3', 'psm4', 'psm5', 'psm6', 'psm7', 'psm8', 'psm9', 'psm10',
  'apk', 'app', 'deb', 'dmg', 'pkg', 'rpm', 'msi', 'appx', 'appxbundle', 'appxupload', 'msix', 'msixbundle', 'msixupload', 'appxbundle', 'appxupload', 'msixbundle', 'msixupload',
  'exe',
  'bat', 'cmd', 'sh',
  'com',
  'scr',
  'ps1',
  'dmg',
  'pkg',
  'iso',
  'img',
  'vhd',
  'vhdx',
  'apks',
  'dll',
  'xapk',
  'ipa',
  'jar',
  'class',
  'pyc',
  'pyo',
  'rb',
  'pl',
  'bin',
  'run',
  'elf',
  'so',
  'sys',
  'drv',
  'crx',
  'xpi',
  //'ufdr',
  'zip',
  'rar',
  '7z',
  'tar',
  'gz',
  'bz2',
  'json',
  'js',
  'ts',
  'css',
];

export const BLANK_SLIDE: Slide = {
  show: true,
  id: 'blank',
  displayOrder: 0,
  order: 0,
  date: '',
  time: '',
  slideTitle: '',
  slideSubTitle: '',
  slideDescription: '',
};

// LLM-related constants
export const REFUSAL_MESSAGES = [
  "Warning:",
  "Sorry, I am unable to assist you with this request",
  "I am unable to assist you",
  "I apologize, but",
  "I cannot assist",
  "I must decline",
  "I cannot provide",
  "I cannot answer",
  "I cannot assist with",
  "I cannot help with",
  "I cannot help you with",
  "I will not",
  "I cannot and should not"
];
export const PASS_MESSAGES = [
  "I apologize, but I do not see",
];
export const TEXT_RESPONSE_PART_DELIMITER = '</p><p>\n';
export const OVERHEAD_PER_PAIR = '<question></question><answer></answer>'.length;

export const PRIMARY_MODEL: { model: string, type: string } = {
  model: 'us.anthropic.claude-sonnet-4-20250514-v1:0',
  type: 'inference-profile'
};
export const FALLBACK_MODEL = (SYSTEM_STAGE: string): { model: string, type: string } => {
  let model = 't6bxo4ur160f';
  if (SYSTEM_STAGE === 'prod') {
    model = 't6bxo4ur160f';
  } else  if (SYSTEM_STAGE === 'staging') {
    model = 'x2j9460kaxwn';
  } else if (SYSTEM_STAGE === 'dev') {
    model = '64klev40k2ur';
  }
  return {
    model: model,
    type: 'imported-model'
  };
}

export const MODEL_ARNS_LIST = (systemStage: string) => {
  const modelArnsListHardcoded: { [key: string]: { primary: { model: string, type: string }, secondary: { model: string, type: string }, tertiary: { model: string, type: string } } } = {
    prod: {
      primary: { ...PRIMARY_MODEL },
      secondary: { ...FALLBACK_MODEL('prod') },
      tertiary: { ...PRIMARY_MODEL }
    },
    stage: {
      primary: { ...PRIMARY_MODEL },
      secondary: { ...FALLBACK_MODEL('staging') },
      tertiary: { ...PRIMARY_MODEL }
    },
    dev: {
      primary: { ...PRIMARY_MODEL },
      secondary: { ...FALLBACK_MODEL('dev') },
      tertiary: { ...PRIMARY_MODEL }
    }
  };
  modelArnsListHardcoded[systemStage] = {
    "primary": { ...PRIMARY_MODEL },
    "secondary": { ...FALLBACK_MODEL('systemStage') },
    "tertiary": { ...PRIMARY_MODEL }
  };
  return modelArnsListHardcoded;
}

export const PRIMARY_MODEL_ARN = 'arn:aws:bedrock:us-east-1:{{accountId}}:inference-profile/us.anthropic.claude-sonnet-4-20250514-v1:0';
export const FALLBACK_MODEL_ARN = (SYSTEM_STAGE: string): string => {
  if (SYSTEM_STAGE === 'prod') {
    return 'arn:aws:bedrock:us-east-1:975050132844:imported-model/t6bxo4ur160f';
  } else  if (SYSTEM_STAGE === 'staging') {
    return 'arn:aws:bedrock:us-east-1:851725433553:imported-model/x2j9460kaxwn';
  } else if (SYSTEM_STAGE === 'dev') {
    return 'arn:aws:bedrock:us-east-1:637423164985:imported-model/64klev40k2ur';
  } else {
    return PRIMARY_MODEL_ARN;
  }
}

export const MARKDOWN_DIRECTIVE = "Please provide the answer in Markdown format using styling to make the answer more readable, as appropriate.";

export const PROMPT_TEMPLATE_BASE = `\r\n
You are a question answering agent. I will provide you with a set of search results. The user will provide you with a question.
Your job is to answer the user's question using the information from the search results.
If the search results do not contain complete information that can answer the question, please try to analyze the data you have to perform an analysis.
If you provide analysis and inference, tell the user. 
Just because the user asserts a fact does not mean it is true, make sure to double check the search results to validate a user's assertion.

CRITICAL: You must structure your response exactly according to the XML format specified below, with no deviations:
- Include all opening AND closing XML tags
- Maintain proper XML tag nesting
- Use only the specified XML tags
- Do not modify or skip any required tags
- Each answer part must be wrapped in <answer_part> tags
- Each answer part must contain both <text> and <sources> sections
\r\n\r\nHere are the search results in numbered order:\r\n$search_results$\r\n\r\n ${MARKDOWN_DIRECTIVE} \r\n\r\n$output_format_instructions$`;

export const PROMPT_TEMPLATE_QUERY = `Here is the user's query:\r\n$query$`;

export const PROMPT_TEMPLATE = `${PROMPT_TEMPLATE_BASE} ${PROMPT_TEMPLATE_QUERY}`;

export const CONVERSATION_CONTEXT = `\r\n\r\n
The prior conversation history follows and is in the form of <pairs><question>user question</question> <answer>generated answer</answer></pairs><pairs><question>user question 2</question> <answer>generated response 2</answer></pairs>
Here is the context:\r\n$context$`;

export const PROMPT_TEMPLATE_WITH_CONTEXT = `${PROMPT_TEMPLATE_BASE} ${CONVERSATION_CONTEXT} ${PROMPT_TEMPLATE_QUERY}`;

export const ORCHESTRATION_PROMPT_TEMPLATE = `\r\n
You are a query creation agent. The user will provide you a question, and your job is to determine the optimal query to use based on the user's question. \r\n
Here are a few examples of queries formed by other search function selection and query creation agents: \r\n\r\n
<examples>\r\n
  <example>\r\n
    <question> What if my vehicle is totaled in an accident? </question>\r\n
    <generated_query> what happens if my vehicle is totaled </generated_query>\r\n
  </example>\r\n 
  <example>\r\n
    <question> I am relocating within the same state. Can I keep my current agent? </question>\r\n
    <generated_query> can I keep my current agent when moving in state </generated_query>\r\n
  </example>\r\n
</examples> \r\n  \r\n
You should also pay attention to the conversation history between the user and the search engine in order to gain the context necessary to create the query. \r\n
Here's another example that shows how you should reference the conversation history when generating a query:\r\n\r\n
  <example>\r\n
    <example_conversation_history>\r\n
      <example_conversation>\r\n
        <question> How many vehicles can I include in a quote in Kansas </question>\r\n
        <answer> You can include 5 vehicles in a quote if you live in Kansas </answer>\r\n
      </example_conversation>\r\n
      <example_conversation>\r\n
        <question> What about texas? </question>\r\n
        <answer> You can include 3 vehicles in a quote if you live in Texas </answer>\r\n
      </example_conversation>\r\n
    </example_conversation_history>\r\n
  </example> \r\n\r\n
Additionally, when generating queries, you must expand common variations for:

1. Law enforcement acronyms and terminology: Include both acronym and full phrase
   Example: "POI suspect" → "POI person of interest suspect"
   
2. Geographic locations: Include common variations
   Example: "incident in CA" → "incident in CA California"

3. Structured data formats when detected:
   - Phone numbers: (XXX) XXX-XXXX, XXXXXXXXXX, XXX-XXX-XXXX
   - IP addresses: XXX.XXX.XXX.XXX, XXX_XXX_XXX_XXX
   - Coordinates: DD.DDDD°N/S DD.DDDD°E/W, DD°MM'SS"N/S DD°MM'SS"E/W
   - Language codes: en-US, en_US, English (United States)

<examples>
  <example>
    <question> Any CTC activity near DC? </question>
    <generated_query> CTC Counterterrorist Center activity near DC District of Columbia Washington </generated_query>
  </example>
  <example>
    <question> ESU responding to 123.45.67.89 </question>
    <generated_query> ESU Emergency Service Unit responding to 123.45.67.89 123_45_67_89 </generated_query>
  </example>
  <example>
    <question> POI contacted (555) 123-4567 from NYC </question>
    <generated_query> POI person of interest contacted 5551234567 555-123-4567 (555) 123-4567 from NYC New York City </generated_query>
  </example>
</examples>\r\n
IMPORTANT: the elements in the <example> tags should not be assumed to have been provided to you to use UNLESS they are also explicitly given to you below. \r\n
All of the values and information within the examples (the questions, answers, and function calls) are strictly part of the examples and have not been provided to you. \r\n\r\n
Here are the search results in numbered order:\r\n$search_results$\r\n\r\n 
Here is the current conversation history: \r\n$conversation_history$\r\n\r\n$output_format_instructions$\r\n\r\n
Here is the user's query:\r\n$query$
"""
${PROMPT_TEMPLATE_QUERY}
`;

export const TEMPERATURE = 0;
export const UNALIGNED_TEMPERATURE = 0.6;
export const MAX_TOKENS = 2048;
export const NUM_OF_RESULTS = 100;
export const OVERRIDE_SEARCH_TYPE = 'HYBRID';
export const SUPPORTED_QUERY_TRANSFORMATION_CONFIG_TYPES = ['QUERY_DECOMPOSITION'];
export const HISTORY_WORD_LIMIT = 80000; // Llama 3.2 has a context window of 128k, but we want to leave some buffer for the prompt and overhead. Using 1.3 tokens per word.
// TODO: Refactor at a later point to calculate the buffer for conversation history rather than hardcoding.
export const HISTORY_CHAR_LIMIT = 50000; // Bedrock has a system prompt limit of 100K characters, so we need to limit the history to 50K characters to leave room for other system prompt elements, most notably the search results.

export const LLM_CONFIG: LLMConfig = {
  refusalMessages: REFUSAL_MESSAGES,
  textResponsePartDelimiter: TEXT_RESPONSE_PART_DELIMITER,
  overheadPerPair: OVERHEAD_PER_PAIR,
  markdownDirective: MARKDOWN_DIRECTIVE,
  promptTemplate: PROMPT_TEMPLATE,
  conversationContext: CONVERSATION_CONTEXT,
  promptTemplateBase: PROMPT_TEMPLATE_BASE,
  promptTemplateQuery: PROMPT_TEMPLATE_QUERY,
  promptTemplateWithContext: PROMPT_TEMPLATE_WITH_CONTEXT,
  orchestrationPromptTemplate: ORCHESTRATION_PROMPT_TEMPLATE,
  historyWordLimit: HISTORY_WORD_LIMIT,
  historyCharLimit: HISTORY_CHAR_LIMIT,
  temperature: TEMPERATURE,
  unalignedTemperature: UNALIGNED_TEMPERATURE,
  maxTokens: MAX_TOKENS,
  numOfResults: NUM_OF_RESULTS,
  overrideSearchType: OVERRIDE_SEARCH_TYPE,
  supportedQueryTransformationConfigTypes: SUPPORTED_QUERY_TRANSFORMATION_CONFIG_TYPES,
  passMessages: PASS_MESSAGES,
  topP: 1,
};
